'use client';

import { Root } from '@radix-ui/react-tabs';
import {
    ComponentPropsWithRef,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { cn } from '@/shared/lib/cn';
import { TabsContent } from './tabs-content';
import { TabsContext } from './tabs-context';
import { TabsList } from './tabs-list';
import { TabsTrigger } from './tabs-trigger';
import './tabs.scss';

const TabsRoot = <T extends string>({
    children,
    className,
    onValueChange,
    ...props
}: Omit<ComponentPropsWithRef<typeof Root>, 'value' | 'onValueChange'> & {
    value?: T;
    onValueChange?: (value: T) => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const transitionRef = useRef<NodeJS.Timeout | null>(null);
    const valueChangedRef = useRef(false);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const updateCSSVariables = useCallback((isResize = false) => {
        if (!ref.current) {
            return;
        }

        const tablistElement = ref.current.querySelector('div[data-testid=tabs-list-container]')!;
        const activeElement = ref.current.querySelector('button[data-state=active]')!;

        if (!tablistElement || !activeElement) {
            return;
        }

        const tablistElementRect = tablistElement.getBoundingClientRect();
        const activeElementRect = activeElement.getBoundingClientRect();

        ref.current.style.setProperty('--selectedItemWidth', `${activeElementRect.width}px`);
        ref.current.style.setProperty(
            '--selectedItemOffset',
            `${tablistElement.scrollLeft + activeElementRect.x - tablistElementRect.x}px`,
        );
        if (isResize) {
            ref.current.style.setProperty('--transition', `none`);
            if (transitionRef.current) {
                clearTimeout(transitionRef.current);
            }
            transitionRef.current = setTimeout(() => {
                ref.current?.style?.removeProperty('--transition');
            }, 100);
        }
    }, []);
    const [clickedValue, setClickedValue] = useState<string>();
    const handleValueChange = useCallback(
        (value: T) => {
            onValueChange?.(value);
            setClickedValue(value);
        },
        [onValueChange],
    );

    // Po commitu nového renderu (data-state=active už je na správném tabu),
    // synchronně před paintem přepočítáme pozici "pillu" => okamžité překliknutí.
    // Klíčujeme na props.value (controlled, vč. programové změny) i clickedValue (uncontrolled).
    useLayoutEffect(() => {
        valueChangedRef.current = true;
        resizeObserverRef.current?.disconnect();
        updateCSSVariables();
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            if (ref.current) {
                const activeTab = ref.current.querySelector('button[role=tab][data-state=active]');
                if (activeTab) {
                    resizeObserverRef.current?.observe(activeTab);
                }
            }
        }, 300);
    }, [props.value, clickedValue, updateCSSVariables]);

    useEffect(() => {
        const observer = new ResizeObserver(() => {
            updateCSSVariables(!valueChangedRef.current);
            valueChangedRef.current = false;
        });
        resizeObserverRef.current = observer;

        if (!ref.current) {
            return;
        }
        const activeElement = ref.current.querySelector('button[role=tab][data-state=active]');
        if (!activeElement) {
            return;
        }
        const resizeObserver = resizeObserverRef.current;
        resizeObserver.observe(activeElement);

        return () => {
            observer.disconnect();
            resizeObserverRef.current = null;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (transitionRef.current) {
                clearTimeout(transitionRef.current);
                transitionRef.current = null;
            }
        };
    }, [updateCSSVariables]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (transitionRef.current) {
                clearTimeout(transitionRef.current);
                transitionRef.current = null;
            }
        };
    }, []);

    return (
        <TabsContext.Provider
            value={useMemo(
                () => ({
                    updateCSSVariables,
                }),
                [updateCSSVariables],
            )}
        >
            <Root
                ref={ref}
                className={cn('tabs__wrapper', className)}
                onValueChange={handleValueChange as (value: string) => void}
                {...props}
            >
                {children}
            </Root>
        </TabsContext.Provider>
    );
};

export const Tabs = Object.assign(TabsRoot, {
    List: TabsList,
    Trigger: TabsTrigger,
    Content: TabsContent,
});

export { TabsRoot as Root };
export { TabsList as List } from './tabs-list';
export { TabsTrigger as Trigger } from './tabs-trigger';
export { TabsContent as Content } from './tabs-content';
